import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, Loader2, Globe, Lock, ExternalLink, Copy, ShieldCheck,
  Search,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { friendlyAlbumName, formatCertNumber, formatCompletionDate } from "@/lib/certificateFormat";

const STATUS_META = {
  claimed:  { label: "Claimed",   tone: "green"  },
  verified: { label: "Verified",  tone: "green"  },
  issued:   { label: "Issued",    tone: "yellow" },
  active:   { label: "Active",    tone: "yellow" },
  revoked:  { label: "Revoked",   tone: "red"    },
  draft:    { label: "Draft",     tone: "gray"   },
};

export default function MyCertificates() {
  const [certs, setCerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/collector-auth/certificates");
      setCerts(r.data.certificates || []);
      setError(null);
    } catch (e) {
      setError("Unable to load your certificates.");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toUpperCase();
    if (!s) return certs || [];
    return (certs || []).filter((c) =>
      (c.cert_number || "").toUpperCase().includes(s) ||
      (c.album_title || "").toUpperCase().includes(s));
  }, [certs, search]);

  return (
    <div className="space-y-6" data-testid="my-certificates-page">
      <div>
        <div className="kicker kicker-red">Certificates</div>
        <h1 className="h-display text-3xl md:text-4xl mt-1" data-testid="my-certificates-heading">
          My Certificates
        </h1>
        <p className="text-sm text-[var(--smoke)] mt-2 max-w-2xl">
          Every CollectorUp certificate associated with your account. Verified by Gil, stored
          permanently, and — when you flip the switch — showcased on your public profile.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="my-certificates-search"
          placeholder="Search by certificate number or album…"
          className="w-full border-2 border-[var(--ink)] rounded-lg pl-9 pr-3 py-2 text-sm bg-white"
        />
      </div>

      {loading ? (
        <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-12 grid place-items-center text-[var(--smoke)]" data-testid="my-certificates-loading">
          <Loader2 className="animate-spin" size={26} />
        </div>
      ) : error ? (
        <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-red-50 p-6 text-sm text-red-900" data-testid="my-certificates-error">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasAny={(certs || []).length > 0} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="my-certificates-list">
          {filtered.map((c) => <CertCard key={c.cert_number} cert={c} onChanged={refresh} />)}
        </div>
      )}
    </div>
  );
}

function CertCard({ cert, onChanged }) {
  const meta = STATUS_META[cert.status] || { label: cert.status, tone: "gray" };
  const [busy, setBusy] = useState(false);
  const isRevoked = cert.verification_status === "revoked";
  const verifyUrl = `/certificates/verify/${cert.cert_number}`;
  const number = formatCertNumber(cert.cert_number);
  const displayAlbum = cert.album_title
    ? (friendlyAlbumName(cert.album_slug) || cert.album_title)
    : "";
  const completedText = formatCompletionDate(cert.completion_date);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/collector-auth/certificates/${cert.cert_number}/visibility`, {
        is_public: !cert.is_public,
      });
      toast.success(cert.is_public ? "Certificate is now private." : "Certificate is now visible on your profile.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update visibility.");
    } finally { setBusy(false); }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}${verifyUrl}`);
    toast.success("Verification link copied.");
  };

  const toneCls = {
    green:  "bg-emerald-100 text-emerald-900 border-emerald-900",
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
    red:    "bg-red-100 text-red-900 border-red-900",
    gray:   "bg-neutral-100 text-[var(--ink)] border-[var(--ink)]",
  }[meta.tone];

  return (
    <div
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-5 shadow-[3px_3px_0_var(--ink)] flex flex-col"
      data-testid={`cert-card-${cert.cert_number}`}
    >
      {/* Verified pill + status */}
      <div className="flex items-start justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 rounded-full border-2 border-emerald-900 bg-emerald-50 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-900">
          <ShieldCheck size={11} /> Verified Album
        </div>
        <span
          data-testid={`cert-card-${cert.cert_number}-status`}
          className={`shrink-0 px-2 py-0.5 rounded-full border-2 text-[9px] uppercase tracking-widest font-bold ${toneCls}`}
        >
          {meta.label}
        </span>
      </div>

      {/* Owned-by-you hero */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--smoke)]">Collector</div>
        <div className="mt-1 font-display font-bold text-2xl text-[var(--ink)] leading-tight" data-testid={`cert-card-${cert.cert_number}-collector`}>
          Owned by You
        </div>
      </div>

      {/* Certificate number */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--smoke)]">Certificate Number</div>
        <div className="mt-1 font-mono font-bold text-lg text-[var(--panini-red)] break-all" data-testid={`cert-card-${cert.cert_number}-number`}>
          {number}
        </div>
      </div>

      {/* Album + completion */}
      <div className="mt-4 text-sm space-y-1">
        {displayAlbum && (
          <div>
            <span className="text-[var(--smoke)] text-[10px] uppercase tracking-[0.24em] font-bold">Album:</span>{" "}
            <span className="font-bold" data-testid={`cert-card-${cert.cert_number}-album`}>{displayAlbum}</span>
          </div>
        )}
        {completedText && (
          <div className="text-xs text-[var(--smoke)]">
            <span className="uppercase tracking-[0.24em] font-bold">Completed:</span> {completedText}
          </div>
        )}
        {cert.issued_at && (
          <div className="text-xs text-[var(--smoke)]">
            <span className="uppercase tracking-[0.24em] font-bold">Issued:</span> {new Date(cert.issued_at).toLocaleDateString()}
          </div>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-[var(--stone)] flex flex-wrap items-center gap-2">
        <Link
          to={`/dashboard/certificates/${cert.cert_number}`}
          data-testid={`cert-card-${cert.cert_number}-view`}
          className="btn-red text-xs"
        >
          View Certificate
        </Link>
        <a
          href={verifyUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`cert-card-${cert.cert_number}-verify`}
          className="btn-ghost text-xs"
        >
          <ExternalLink size={12} /> Verification page
        </a>
        <button
          type="button"
          onClick={copy}
          data-testid={`cert-card-${cert.cert_number}-copy`}
          className="btn-ghost text-xs"
        >
          <Copy size={12} /> Copy link
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={toggle}
          disabled={busy || isRevoked}
          data-testid={`cert-card-${cert.cert_number}-visibility`}
          aria-pressed={cert.is_public}
          title={isRevoked ? "Revoked certificates cannot be shown." : (cert.is_public ? "Visible on your public profile" : "Private — not shown")}
          className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border-2 inline-flex items-center gap-1 transition disabled:opacity-40 ${
            cert.is_public
              ? "bg-emerald-100 border-emerald-900 text-emerald-900"
              : "bg-white border-[var(--ink)] text-[var(--smoke)]"
          }`}
        >
          {busy
            ? <Loader2 size={10} className="animate-spin" />
            : (cert.is_public ? <Globe size={10} /> : <Lock size={10} />)}
          {cert.is_public ? "Visible on Profile" : "Private"}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ hasAny }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-[var(--ink)] bg-white p-10 text-center" data-testid="my-certificates-empty">
      <Award size={36} className="mx-auto text-[var(--smoke)]" />
      <div className="font-display text-xl mt-3">
        {hasAny ? "No certificates match your search." : "No certificates have been associated with your CollectorUp account yet."}
      </div>
      <p className="text-sm text-[var(--smoke)] mt-2 max-w-md mx-auto">
        {hasAny
          ? "Try clearing the search or use the exact certificate number."
          : "When Gil issues a certificate for one of your finished albums, it will appear here automatically."}
      </p>
    </div>
  );
}
